import * as moment from 'moment';
import { environment } from '@env/environment';

/**
 * EcTimelineEnhancer
 *
 * Pure TypeScript extension layer over an @event-calendar/build instance.
 * No Angular dependencies — can be used with any EventCalendar resource-timeline.
 *
 * Responsibilities:
 *   - Dynamic slot-width calculation so the visible zoom range always fills the
 *     container width (ResizeObserver + rAF debounce, handles CSS transitions)
 *   - Scroll-to-time: positions the scroll container so a given moment is at the
 *     left edge of the viewport + half a slot of breathing room
 *   - Scroll-by-days: pixel-accurate navigation within the continuous canvas
 *   - Visible-date tracking: reports the date at the left scroll edge via callback
 *   - Edge detection: fires when the user scrolls within EDGE_THRESHOLD_DAYS of
 *     the loaded window boundary
 */

// ── Zoom configuration ──────────────────────────────────────────────────────

export type ZoomLevel = 'day' | 'week' | 'month';

// NOTE: All views use { hours: N } not { days: N }.
// EC's createDuration({ days: N }).seconds === 0, so toSeconds() returns 0,
// triggering EC's internal "monthView" branch which renders events as full-slot
// blocks instead of proportionally-sized timeline bars.
// { hours: N } gives toSeconds() = N * 3600, so events are always proportional.
export interface ZoomConfig {
    /** EventCalendar slotDuration. */
    slotDuration: { hours: number };
    /** Total slots to fill the viewport width (= visibleDays × slotsPerDay). */
    visibleSlots: number;
    /** How many days ‹/› navigation moves. */
    navDays: number;
}

export const ZOOM_CONFIGS: Record<ZoomLevel, ZoomConfig> = {
    //           slotDuration         visibleSlots  navDays
    day:   { slotDuration: { hours: 24 }, visibleSlots:  1, navDays:  1 },
    week:  { slotDuration: { hours: 24 }, visibleSlots:  7, navDays:  7 },
    month: { slotDuration: { hours: 24 }, visibleSlots: 30, navDays: 30 },
};

/**
 * Maps zoom level to EC view name.
 * Day uses resourceTimelineDay (1-day native canvas) — resourceTimelineWeek
 * with slotDuration { hours: 2 } keeps --ec-grid-cols at 60 (days) and renders
 * time sub-slots vertically inside each day column rather than as timeline columns.
 * Week and month both use the 60-day resourceTimelineWeek canvas.
 */
export const EC_VIEW_NAME: Record<ZoomLevel, string> = {
    day:   'resourceTimelineWeek',
    week:  'resourceTimelineWeek',
    month: 'resourceTimelineWeek',
};







// ── Constants ────────────────────────────────────────────────────────────────

/** Width of the resource-label column — must match resourceAreaWidth in calendar init.
 *  Includes the 60px left inset applied via padding-left on .ec-row-head. */
export const RESOURCE_AREA_WIDTH_PX = 320;

/** Total days covered by the loaded data window. Sourced from environment.plannerWindowDays. */
export const WINDOW_DAYS = environment.plannerWindowDays;

/** snapDuration applied globally — fine-grained drag precision independent of zoom. */
export const SNAP_DURATION = { minutes: 15 };

/** Minimum rendered slot width in pixels (prevents sub-pixel collapse). */
const MIN_SLOT_WIDTH_PX = 5;

/** Days from either window edge that triggers onEdgeApproached. */
const EDGE_THRESHOLD_DAYS = 5;

// ── Types ────────────────────────────────────────────────────────────────────

export interface EnhancerState {
    zoom: ZoomLevel;
    showForm: boolean;
    /** If the booking form is open, the start time of the pending reservation. */
    formBegin?: Date;
    /** UTC midnight of the first day of the loaded 60-day window. */
    windowStart: Date;
}

export interface EcTimelineEnhancerConfig {
    ec: any;
    containerEl: HTMLElement;
    /** Called every rAF cycle when the container resizes or zoom changes. */
    getState: () => EnhancerState;
    /** Called (outside Angular zone) when the visible left-edge date changes. */
    onDateChange: (date: Date) => void;
    /** Called when scroll approaches within EDGE_THRESHOLD_DAYS of either edge. */
    onEdgeApproached?: (visibleDate: Date) => void;
}

// ── Class ────────────────────────────────────────────────────────────────────

export class EcTimelineEnhancer {

    private rafId: number | null = null;
    private observer: any = null;
    private _slotWidth = 32;

    private readonly ec: any;
    private readonly containerEl: HTMLElement;
    private readonly getState: () => EnhancerState;
    private readonly onDateChange: (date: Date) => void;
    private readonly onEdgeApproached?: (date: Date) => void;

    constructor(cfg: EcTimelineEnhancerConfig) {
        this.ec               = cfg.ec;
        this.containerEl      = cfg.containerEl;
        this.getState         = cfg.getState;
        this.onDateChange     = cfg.onDateChange;
        this.onEdgeApproached = cfg.onEdgeApproached;

        // Observe the PARENT of the calendar container — that element's width is
        // constrained by the flex layout and responds to window resizing.
        // The containerEl itself may be as wide as the full canvas (60 days ×
        // slotWidth), so observing it would not fire on window shrink.
        const RO = (window as any).ResizeObserver;
        if (RO) {
            this.observer = new RO(() => this.scheduleUpdate());
            this.observer.observe(this.containerEl.parentElement ?? this.containerEl);
        }

        // Defer scroll-listener attachment until EventCalendar has rendered its DOM.
        this.attachScrollListener();
    }

    // ── Public API ───────────────────────────────────────────────────────────

    get slotWidth(): number { return this._slotWidth; }

    /**
     * Switch to the EC view for this zoom level and recalculate slot width.
     * Day zoom uses resourceTimelineDay (1-day canvas) to get proper horizontal
     * 2h-slot columns. Week/month use the 60-day resourceTimelineWeek canvas.
     * slotDuration is set explicitly because the views-config per-view override
     * is not reliably applied when duration is also customised.
     */
    setZoom(zoom: ZoomLevel): void {
        this.ec.setOption('slotDuration', ZOOM_CONFIGS[zoom].slotDuration);
        this.ec.setOption('view', EC_VIEW_NAME[zoom]);
        this.recalcSlotWidth();
    }

    /**
     * Recompute slotWidth from current container size and zoom, and push the
     * new value to EventCalendar. Called automatically by the ResizeObserver;
     * call manually after a zoom change or programmatic layout change.
     */
    recalcSlotWidth(): void {
        const { zoom } = this.getState();
        const cfg = ZOOM_CONFIGS[zoom];
        // Measure from the parent wrapper — it is flex-constrained to the viewport.
        // containerEl may be wider than the viewport (full canvas width).
        const measureEl = this.containerEl.parentElement ?? this.containerEl;
        const available = Math.max(measureEl.offsetWidth - RESOURCE_AREA_WIDTH_PX, 0);
        this._slotWidth = Math.max(Math.floor(available / cfg.visibleSlots), MIN_SLOT_WIDTH_PX);
        this.ec.setOption('slotWidth', this._slotWidth);
    }

    /**
     * Scroll so that `date` is at the left edge of the viewport.
     * `offsetSlots` shifts the result left by that many slot-widths:
     *   - 0   → date exactly at left edge (period-start navigation, today button)
     *   - 0.5 → date at left edge + half slot (breathing room after event creation)
     */
    scrollToDate(date: Date, offsetSlots = 0.5): void {
        const el = this.scrollEl();
        if (!el) { return; }

        const { zoom, windowStart } = this.getState();
        const cfg = ZOOM_CONFIGS[zoom];
        const offsetMs = date.getTime() - windowStart.getTime();
        if (offsetMs < 0) { return; }

        const pixelX = (offsetMs / this.slotMs(cfg.slotDuration)) * this._slotWidth;
        el.scrollLeft = Math.max(0, pixelX - this._slotWidth * offsetSlots);
    }

    /**
     * Return the calendar date currently visible at the left edge of the
     * scroll viewport.
     */
    visibleDate(): Date {
        const el = this.scrollEl();
        const { zoom, windowStart } = this.getState();
        if (!el) { return windowStart; }
        const { slotDuration } = ZOOM_CONFIGS[zoom];
        const offsetMs = (el.scrollLeft / this._slotWidth) * this.slotMs(slotDuration);
        // Round to nearest local-day boundary — all zoom levels use 24 h slots so
        // the scroll position is always within minutes of a midnight. Adding 12 h
        // before flooring ensures sub-day float / UTC-vs-local drift doesn't push
        // us into the previous day (e.g. 23:58 → +12 h → 11:58 next day → floor → today).
        return moment(new Date(windowStart.getTime() + offsetMs)).add(12, 'hours').startOf('day').toDate();
    }

    destroy(): void {
        this.observer?.disconnect();
        this.observer = null;
        if (this.rafId !== null) {
            cancelAnimationFrame(this.rafId);
            this.rafId = null;
        }
    }

    // ── Private ──────────────────────────────────────────────────────────────

    /**
     * rAF-debounced update: recalculates slot width then, if the booking form
     * is open, scrolls to keep the pending event's start in view.
     */
    private scheduleUpdate(): void {
        if (this.rafId !== null) { cancelAnimationFrame(this.rafId); }
        this.rafId = requestAnimationFrame(() => {
            this.rafId = null;
            // Snapshot the visible date before slot width changes so the pixel
            // scroll position can be restored after EC re-renders at the new width.
            const anchor = this.visibleDate();
            this.recalcSlotWidth();
            const { showForm, formBegin } = this.getState();
            if (showForm && formBegin) {
                this.scrollToDate(formBegin);
            } else {
                this.scrollToDate(anchor, 0);
            }
        });
    }

    private attachScrollListener(): void {
        requestAnimationFrame(() => {
            const el = this.scrollEl();
            if (el) {
                el.addEventListener('scroll', () => this.onScroll(), { passive: true });
            }
        });
    }

    private onScroll(): void {
        const date = this.visibleDate();
        this.onDateChange(date);
        if (this.onEdgeApproached) { this.checkEdge(date); }
    }

    private checkEdge(visibleDate: Date): void {
        const { windowStart } = this.getState();
        const thresholdMs   = EDGE_THRESHOLD_DAYS * 86_400_000;
        const windowEndMs   = windowStart.getTime() + WINDOW_DAYS * 86_400_000;
        const nearStart     = visibleDate.getTime() - windowStart.getTime() < thresholdMs;
        const nearEnd       = windowEndMs - visibleDate.getTime() < thresholdMs;
        if (nearStart || nearEnd) {
            this.onEdgeApproached!(visibleDate);
        }
    }

    /** Find the EventCalendar horizontal scroll container.
     *  EventCalendar CSS sets `overflow: auto` on `.ec-main`, NOT `.ec-body`.
     *  `.ec-body` is a subgrid child of `.ec-main` and does not scroll itself.
     */
    private scrollEl(): HTMLElement | null {
        return this.containerEl.querySelector('.ec-main') as HTMLElement | null;
    }

    private slotMs(slotDuration: { hours?: number; days?: number; minutes?: number }): number {
        if (slotDuration.days)    { return slotDuration.days    * 86_400_000; }
        if (slotDuration.hours)   { return slotDuration.hours   *  3_600_000; }
        if (slotDuration.minutes) { return slotDuration.minutes *     60_000; }
        return 3_600_000;
    }
}
